import { useEffect, useState, useCallback } from "react";

export type ProBranding = {
  company_name: string | null;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  /** Logo armazenada como data URL (base64) no localStorage. */
  logo_url: string | null;
  footer_note: string | null;
};

const STORAGE_KEY = "mt:branding";

const EMPTY: ProBranding = {
  company_name: null,
  cnpj: null,
  email: null,
  phone: null,
  website: null,
  address: null,
  logo_url: null,
  footer_note: null,
};

function read(): ProBranding | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return { ...EMPTY, ...JSON.parse(raw) } as ProBranding;
  } catch {
    return null;
  }
}

export function saveBranding(b: ProBranding) {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
  window.dispatchEvent(new Event("mt:branding-changed"));
}

export function clearBranding() {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new Event("mt:branding-changed"));
}

export function useProBranding() {
  const [branding, setBranding] = useState<ProBranding | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setBranding(read());
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("mt:branding-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("mt:branding-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, [refresh]);

  return { branding, loading, refetch: refresh };
}

/**
 * Compatibilidade: a logo agora já é uma data URL.
 * Mantemos a função para o gerador de PDFs continuar funcionando.
 */
export async function loadLogoDataUrl(logoPath: string | null): Promise<string | null> {
  if (!logoPath) return null;
  if (logoPath.startsWith("data:")) return logoPath;
  return null;
}
